import { CODE_FORWARD_SLASH, CODE_HASH, CODE_QUESTION_MARK } from './const';
import ImmutableURL from './immutable-url';
import URLSearchParamsWrapper from './search-params-wrapper';
function mutate(url, changes) {
    const self = {
        hash: changes.hash !== undefined ? changes.hash : url.hash,
        host: changes.host !== undefined ? changes.host : url.host,
        hostname: changes.hostname !== undefined ? changes.hostname : url.hostname,
        password: changes.password !== undefined ? changes.password : url.password,
        pathname: changes.pathname !== undefined ? changes.pathname : url.pathname,
        port: changes.port !== undefined ? changes.port : url.port,
        protocol: changes.protocol !== undefined ? changes.protocol : url.protocol,
        search: changes.search !== undefined ? changes.search : url.search,
        username: changes.username !== undefined ? changes.username : url.username,
    };
    if (changes.hostname || changes.port) {
        if (self.protocol === 'https:' && self.port === '443') {
            self.port = '';
        }
        else if (self.protocol === 'http:' && self.port === '80') {
            self.port = '';
        }
        self.host = `${self.hostname}${self.port ? ':' : ''}${self.port}`;
    }
    const user = self.username
        ? self.password
            ? `${self.username}:${self.password}@`
            : `${self.username}@`
        : self.password
            ? `:${self.password}@`
            : '';
    return new ImmutableURL(`${self.protocol}${url.slashes}${user}${self.host}${self.pathname}${self.search}${self.hash}`);
}
export default class {
    constructor(url) {
        this.url = new ImmutableURL(url);
    }
    get protocol() {
        return this.url.protocol;
    }
    set protocol(value) {
        const previousProtocol = this.url.protocol;
        const colon = value.endsWith(':') ? '' : ':';
        const href = `${value}${colon}${this.href.slice(previousProtocol.length)}`;
        this.url = new ImmutableURL(href);
    }
    get username() {
        return this.url.username;
    }
    set username(value) {
        this.url = mutate(this, {
            username: value || '',
        });
    }
    get password() {
        return this.url.password;
    }
    set password(value) {
        this.url = mutate(this, {
            password: value || '',
        });
    }
    get hostname() {
        return this.url.hostname;
    }
    set hostname(value) {
        this.url = mutate(this, {
            hostname: value || '',
        });
    }
    get host() {
        return this.url.host;
    }
    set host(value) {
        this.url = mutate(this, {
            host: value,
        });
    }
    get origin() {
        return this.url.origin;
    }
    get port() {
        return this.url.port;
    }
    set port(value) {
        this.url = mutate(this, {
            port: value || '',
        });
    }
    get pathname() {
        return this.url.pathname;
    }
    set pathname(value) {
        const pathname = value.charCodeAt(0) === CODE_FORWARD_SLASH ? value : `/${value}`;
        this.url = mutate(this, {
            pathname,
        });
    }
    /**
     * The query string component of the URL, including the preceding `?` character.
     * See also: https://developer.mozilla.org/en-US/docs/Web/API/URL/search
     */
    get search() {
        return this.url.search;
    }
    set search(value) {
        const newQuery = value.charCodeAt(0) === CODE_QUESTION_MARK ? value.slice(1) : value;
        this.url = mutate(this, {
            search: newQuery.length > 0 ? `?${newQuery}` : '',
        });
    }
    /**
     * Parsed query string parameters, as a `URLSearchParams` object.
     * See also: https://developer.mozilla.org/en-US/docs/Web/API/URL/searchParams
     */
    get searchParams() {
        return new URLSearchParamsWrapper(this, this.url.searchParams);
    }
    /**
     * Parsed parameter string from the url. These are `;` separated key/values appearing in the URL
     * path, before the query string.
     */
    get parameters() {
        return this.url.parameters;
    }
    /**
     * Check if the URL has a parameter string
     * @returns true iff `;` occurs in the URL path before a `?`.
     */
    hasParameterString() {
        return this.url.hasParameterString();
    }
    /**
     * URL hash or fragment component.
     * See also: https://developer.mozilla.org/en-US/docs/Web/API/URL/hash
     */
    get hash() {
        return this.url.hash;
    }
    set hash(value) {
        const newHash = value.charCodeAt(0) === CODE_HASH ? value.slice(1) : value;
        this.url = mutate(this, {
            hash: newHash.length > 0 ? `#${newHash}` : '',
        });
    }
    get href() {
        return this.url.href;
    }
    set href(value) {
        this.url = new ImmutableURL(value);
    }
    /**
     * Returns the url (post parsing).
     * See also: https://developer.mozilla.org/en-US/docs/Web/API/URL/toString
     */
    toString() {
        return this.href;
    }
    /**
     * JSONified URL (== toString)
     * See also: https://developer.mozilla.org/en-US/docs/Web/API/URL/toJSON
     */
    toJSON() {
        return this.href;
    }
    /**
     * Get parsed domainInfo from the hostname.
     * @returns parsed domain, from tldts `parse` method.
     */
    get domainInfo() {
        return this.url.domainInfo;
    }
    /**
     * Returns true iff the hostname of this url is an IP address. False otherwise.
     */
    get hostIsIp() {
        return this.url.hostIsIp;
    }
    /**
     * Returns the hostname of the URL after parsing by tldts. This includes some error correction.
     */
    get domain() {
        return this.url.domain;
    }
    /**
     * Get eTLD+1 of the hostname.
     */
    get generalDomain() {
        return this.url.generalDomain;
    }
    /**
     * Legacy attribute for `pathname`.
     */
    get path() {
        return this.url.path;
    }
    /**
     * Scheme = protocol without a trailing ':'.
     */
    get scheme() {
        return this.url.scheme;
    }
    get slashes() {
        return this.url.slashes;
    }
    /**
     * Check if the hostname of the URL is valid, i.e.
     *  * it is an IP address, or
     *  * it is a valid hostname with a known public suffix.
     * @returns true if host is valid, otherwise false.
     */
    isValidHost() {
        // if tldts was able to parse it, it's valid
        return this.url.isValidHost();
    }
    /**
     * Non-standard params extractor.
     *
     * Returns search params from parameter string and query params with more aggessive extraction
     * than the standard URL implementation. Extra extraction features are:
     *  * `;` separated parameters - used by multi trackers
     * @returns URLSearchParams
     */
    extractKeyValues() {
        return this.url.extractKeyValues();
    }
}
//# sourceMappingURL=url.js.map